// Follows "HR Design rules.md": GEN, SKL
import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-sm border border-transparent px-1.5 py-0.5 text-xs font-medium transition-[color,background-color,box-shadow] duration-fast ease-standard focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background aria-invalid:border-destructive [&>svg]:pointer-events-none [&>svg]:size-3',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground [a&]:hover:bg-primary-hover',
        // The gold brand colour; neutral chips are `outline`, neutral statuses `info-subtle`.
        secondary: 'bg-secondary text-secondary-foreground [a&]:hover:bg-secondary-hover',
        tertiary: 'bg-tertiary text-tertiary-foreground [a&]:hover:bg-tertiary-hover',
        outline:
          'border-border text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
        ghost: '[a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 [a&]:hover:underline',
        // Status, solid
        success: 'bg-success text-success-foreground [a&]:hover:bg-success-hover',
        warning: 'bg-warning text-warning-foreground [a&]:hover:bg-warning-hover',
        info: 'bg-info text-info-foreground [a&]:hover:bg-info-hover',
        destructive: 'bg-destructive text-destructive-foreground [a&]:hover:bg-destructive-hover',
        // Status, subtle (tinted background and border); pair with an icon or text label (GEN-07).
        'success-subtle': 'border-success-border bg-success-subtle text-success-subtle-foreground',
        'warning-subtle': 'border-warning-border bg-warning-subtle text-warning-subtle-foreground',
        'info-subtle': 'border-info-border bg-info-subtle text-info-subtle-foreground',
        'destructive-subtle':
          'border-destructive-border bg-destructive-subtle text-destructive-subtle-foreground',
        // Skill chip: the pastel comes from the `skill` prop (one hue per skill sub-category, SKL-01).
        skill: '',
      },
      skill: {
        frontend: 'border-skill-frontend-border bg-skill-frontend text-skill-frontend-foreground',
        backend: 'border-skill-backend-border bg-skill-backend text-skill-backend-foreground',
        fullstack: 'border-skill-fullstack-border bg-skill-fullstack text-skill-fullstack-foreground',
        devops: 'border-skill-devops-border bg-skill-devops text-skill-devops-foreground',
        qa: 'border-skill-qa-border bg-skill-qa text-skill-qa-foreground',
        ui: 'border-skill-ui-border bg-skill-ui text-skill-ui-foreground',
        ux: 'border-skill-ux-border bg-skill-ux text-skill-ux-foreground',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

/** Skill sub-categories and their display names, from skills_by_category.csv (SKL-04). */
const SKILL_CATEGORIES = {
  frontend: 'Frontend',
  backend: 'Backend',
  fullstack: 'Fullstack',
  devops: 'DevOps',
  qa: 'QA',
  ui: 'UI',
  ux: 'UX',
} as const;

type SkillCategory = keyof typeof SKILL_CATEGORIES;

export type BadgeProps = React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean };

function Badge({ className, variant = 'default', skill, asChild = false, ...props }: BadgeProps) {
  const Comp = asChild ? Slot : 'span';
  const resolved = skill ? 'skill' : variant;

  return (
    <Comp
      data-slot="badge"
      data-variant={resolved}
      data-skill={skill ?? undefined}
      className={cn(badgeVariants({ variant: resolved, skill }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants, SKILL_CATEGORIES, type SkillCategory };
